import { Text } from 'preact-i18n';
import { formatPriceCompact } from '../utils/priceFormatters';
import { getPriceRangeByLevel } from '../utils/priceLevelStats';
import isDateBlocked from '../utils/isDateBlocked';
import NoTripsIcon from './NoTripsIcon';

const LEVELS = ['cheap', 'normal', 'expensive'];

const formatRange = ({ min, max }, formatPrice) => {
  const formattedMin = formatPrice(min);
  const formattedMax = formatPrice(max);

  return formattedMin === formattedMax
    ? formattedMin
    : `${formattedMin} – ${formattedMax}`;
};

/**
 * Calendar price-level legend row.
 *
 * Renders one chip per price level that has at least one available day with a
 * known price. Each chip shows that level's *real* range (`$850 – $920`, or a
 * single value when the level has one price), so the number the buyer reads is
 * one a selectable day of that colour actually carries — the old `$NNN +` form
 * advertised the cheapest day of the window for every day of the colour.
 * The range comes from `priceLevels` (the API's `meta.price_levels` for this
 * leg) when available, and is otherwise folded from `availability`.
 *
 * The "no-trips" indicator is shown whenever at least one day in the range is
 * blocked — `available: false` and settled (`state !== 'pending'`). Chips and
 * "no-trips" coexist (they are not mutually exclusive): a window with both
 * priced and blocked days shows the chips and the indicator after them. The
 * price disclaimer follows whenever at least one chip is present.
 *
 * Returns `null` when there is nothing to show (e.g. while the window is still
 * warming up), so the footer never renders an empty legend row.
 *
 * A leg label on the left states which leg the prices belong to ("Ida:" /
 * "Regreso:"), driven by `leg` ('departure' | 'return').
 *
 * Pass `className` to scope visibility (desktop-only or mobile-only). `formatPrice`
 * formats the chip bounds; it defaults to the MXN compact format.
 */
const PriceLevelLegend = ({
  availability,
  priceLevels = null,
  leg = 'departure',
  className = '',
  formatPrice = formatPriceCompact,
}) => {
  const ranges = getPriceRangeByLevel(availability, priceLevels);
  const visibleLevels = LEVELS.filter(level => ranges[level] !== null);
  const days = Object.values(availability || {});
  const hasPriceChips = visibleLevels.length > 0;
  const showNoTrips = days.length > 0 && days.some(isDateBlocked);

  if (!hasPriceChips && !showNoTrips) return null;

  const legLabelId =
    leg === 'return' ? 'price_level_leg_return' : 'price_level_leg_departure';

  return (
    <div className="udp-continue-footer__legend-row">
      <div className={`udp-price-legend ${className}`.trim()}>
        <div className="udp-price-legend__group">
          <span className="udp-price-legend__leg-label">
            <Text id={`label.${legLabelId}`} />
          </span>
          {visibleLevels.map(level => (
            <span
              key={level}
              className={`udp-price-legend__chip udp-price-legend__chip--${level}`}
            >
              {formatRange(ranges[level], formatPrice)}
            </span>
          ))}
        </div>
        {showNoTrips && (
          <span className="udp-price-legend__no-data">
            <NoTripsIcon className="udp-price-legend__no-data-icon" />
            <Text id="label.price_level_no_trips" />
          </span>
        )}
      </div>
      {hasPriceChips && (
        <p className="udp-continue-footer__disclaimer">
          <Text id="label.price_level_disclaimer" />
        </p>
      )}
    </div>
  );
};

export default PriceLevelLegend;
